import React, { useEffect, useState } from "react";
import apiClient from '../utils/apiClient';
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { User, Mail, Phone, Target, UserPlus, Image, Sun, Moon, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';

/**
 * AdminManagePlayerPage - Manage Players
 *
 * Per GCHL Brand Guide:
 * - Forest green primary with gold accents
 * - Navy dark mode with proper contrast
 * - Card-based layout with proper shadows
 */
const AdminManagePlayerPage = () => {
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    startingQuota: "",
    new_player: "",
    image: null,
  });
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize dark mode from localStorage/system preference
  useEffect(() => {
    const stored = localStorage.getItem('theme');
    if (stored) {
      setIsDarkMode(stored === 'dark');
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setIsDarkMode(true);
    }
  }, []);

  // Sync dark mode to document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await apiClient.get('/admin/admin-players');
        setPlayers(response.data);
      } catch (error) {
        console.error("Error fetching players:", error.message);
      }
    };
    fetchPlayers();
  }, []);

  const handlePlayerChange = (e) => {
    const playerId = e.target.value;
    const player = players.find((p) => p.id === parseInt(playerId));
    setSelectedPlayer(player);

    if (player) {
      setFormData({
        name: player.name,
        email: player.email,
        phone: player.phone_number,
        startingQuota: player.current_quota,
        new_player: player.new_player,
        image: null,
      });
    } else {
      setFormData({
        name: "",
        email: "",
        phone: "",
        startingQuota: "",
        new_player: "",
        image: null,
      });
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : files ? files[0] : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      const formPayload = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key]) formPayload.append(key, formData[key]);
      });

      if (selectedPlayer) {
        await apiClient.put(
          `/admin/admin-players/${selectedPlayer.id}`,
          formPayload,
          {
            headers: { "Content-Type": "multipart/form-data" }
          }
        );
        setFeedbackMessage({
          type: "success",
          text: `Player "${formData.name}" updated successfully!`,
        });
      } else {
        await apiClient.post('/admin/admin-players', formPayload, {
          headers: {
            "Content-Type": "multipart/form-data",
          }
        });
        setFeedbackMessage({
          type: "success",
          text: `Player "${formData.name}" added successfully!`,
        });
      }

      // Refresh player list
      const response = await apiClient.get('/admin/admin-players');
      setPlayers(response.data);
      setSelectedPlayer(null);
      setFormData({
        name: "",
        email: "",
        phone: "",
        startingQuota: "",
        new_player: "",
        image: null,
      });

      // Clear feedback after 3 seconds
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (error) {
      console.error("Error saving player:", error.message);
      setFeedbackMessage({
        type: "error",
        text: "Failed to save player. Please try again.",
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedPlayer) return;

    try {
      await apiClient.delete(`/admin/admin-players/${selectedPlayer.id}`);
      setFeedbackMessage({
        type: "success",
        text: `Player "${selectedPlayer.name}" deleted successfully!`,
      });

      // Refresh player list
      const response = await apiClient.get('/admin/admin-players');
      setPlayers(response.data);
      setSelectedPlayer(null);
      setFormData({
        name: "",
        email: "",
        phone: "",
        startingQuota: "",
        new_player: "",
        image: null,
      });

      // Clear feedback after 3 seconds
      setTimeout(() => setFeedbackMessage(null), 3000);
    } catch (error) {
      console.error("Error deleting player:", error.message);
      setFeedbackMessage({
        type: "error",
        text: "Failed to delete player. Please try again.",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-navy-950 flex flex-col transition-colors duration-300">
      <Navbar />

      {/* Header Section */}
      <div className="bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-navy-800">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-display font-bold text-forest-900 dark:text-white tracking-tight">
                Manage Players
              </h1>
              <p className="text-gray-600 dark:text-navy-400 mt-1">
                {selectedPlayer ? `Editing: ${selectedPlayer.name}` : "Add a new player"}
              </p>
            </div>

            {/* Dark Mode Toggle */}
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="
                flex items-center gap-2 px-4 py-2 rounded-full
                bg-gray-100 dark:bg-navy-800
                text-gray-700 dark:text-gray-200
                hover:bg-gray-200 dark:hover:bg-navy-700
                transition-colors duration-200
                border border-gray-200 dark:border-navy-700
              "
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <>
                  <Sun className="w-5 h-5 text-gold-400" />
                  <span className="text-sm font-medium hidden sm:inline">Light</span>
                </>
              ) : (
                <>
                  <Moon className="w-5 h-5 text-navy-600" />
                  <span className="text-sm font-medium hidden sm:inline">Dark</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow max-w-4xl mx-auto px-4 py-8 w-full">
        {/* Feedback Message */}
        {feedbackMessage && (
          <div
            className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
              feedbackMessage.type === "success"
                ? "bg-seafoam-light/10 text-seafoam-dark dark:bg-seafoam-dark/20 dark:text-seafoam-light border border-seafoam-light/30"
                : "bg-sunset-light/10 text-sunset-dark dark:bg-sunset-dark/20 dark:text-sunset-light border border-sunset-light/30"
            }`}
          >
            {feedbackMessage.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            {feedbackMessage.text}
          </div>
        )}

        <Card>
          {/* Player Selector */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Select Player to Edit
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="w-5 h-5 text-gray-400 dark:text-navy-500" />
              </div>
              <select
                id="playerSelector"
                onChange={handlePlayerChange}
                className="
                  w-full pl-10 pr-4 py-3
                  bg-white dark:bg-navy-800
                  border border-gray-300 dark:border-navy-600
                  rounded-xl
                  text-gray-900 dark:text-gray-100
                  focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                  focus:border-forest-500 dark:focus:border-gold-400
                  transition-colors duration-200
                  appearance-none cursor-pointer
                "
                value={selectedPlayer?.id || ""}
              >
                <option value="">-- Add New Player --</option>
                {players.map((player) => (
                  <option key={player.id} value={player.id}>
                    {player.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg className="w-5 h-5 text-gray-400 dark:text-navy-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-forest-600 dark:text-forest-400" />
                  Name
                </div>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                className="
                  w-full px-4 py-3
                  bg-white dark:bg-navy-800
                  border border-gray-300 dark:border-navy-600
                  rounded-xl
                  text-gray-900 dark:text-gray-100
                  focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                  focus:border-forest-500 dark:focus:border-gold-400
                  transition-colors duration-200
                "
                required
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-forest-600 dark:text-forest-400" />
                  Email
                </div>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleFormChange}
                className="
                  w-full px-4 py-3
                  bg-white dark:bg-navy-800
                  border border-gray-300 dark:border-navy-600
                  rounded-xl
                  text-gray-900 dark:text-gray-100
                  focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                  focus:border-forest-500 dark:focus:border-gold-400
                  transition-colors duration-200
                "
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-forest-600 dark:text-forest-400" />
                  Phone
                </div>
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleFormChange}
                className="
                  w-full px-4 py-3
                  bg-white dark:bg-navy-800
                  border border-gray-300 dark:border-navy-600
                  rounded-xl
                  text-gray-900 dark:text-gray-100
                  focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                  focus:border-forest-500 dark:focus:border-gold-400
                  transition-colors duration-200
                "
              />
            </div>

            {/* Starting Quota */}
            <div>
              <label htmlFor="startingQuota" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-forest-600 dark:text-forest-400" />
                  Starting Quota
                </div>
              </label>
              <input
                type="number"
                id="startingQuota"
                name="startingQuota"
                value={formData.startingQuota}
                onChange={handleFormChange}
                className="
                  w-full px-4 py-3
                  bg-white dark:bg-navy-800
                  border border-gray-300 dark:border-navy-600
                  rounded-xl
                  text-gray-900 dark:text-gray-100
                  focus:ring-2 focus:ring-forest-500 dark:focus:ring-gold-400
                  focus:border-forest-500 dark:focus:border-gold-400
                  transition-colors duration-200
                "
                required
              />
            </div>

            {/* New Player Checkbox */}
            <div className="bg-gray-50 dark:bg-navy-800/50 rounded-xl p-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.new_player}
                  id="new_player"
                  name="new_player"
                  onChange={handleFormChange}
                  className="
                    w-5 h-5 rounded border-gray-300 dark:border-navy-600
                    text-forest-600 focus:ring-forest-500
                    dark:bg-navy-700
                  "
                />
                <div className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-forest-600 dark:text-forest-400" />
                  <div>
                    <span className="font-medium text-gray-900 dark:text-gray-100">New Player to Group</span>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Quota adjustment caps apply for first 2 events</p>
                  </div>
                </div>
              </label>
            </div>

            {/* Player Image */}
            <div>
              <label htmlFor="image" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <div className="flex items-center gap-2">
                  <Image className="w-4 h-4 text-forest-600 dark:text-forest-400" />
                  Player Image
                </div>
              </label>
              <input
                type="file"
                id="image"
                name="image"
                accept="image/*"
                onChange={handleFormChange}
                className="
                  w-full px-4 py-3
                  bg-white dark:bg-navy-800
                  border border-gray-300 dark:border-navy-600
                  rounded-xl
                  text-gray-900 dark:text-gray-100
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:bg-forest-100 file:text-forest-700
                  dark:file:bg-forest-900/40 dark:file:text-forest-400
                  file:font-semibold file:text-sm
                  hover:file:bg-forest-200 dark:hover:file:bg-forest-900/60
                  file:cursor-pointer
                  transition-colors duration-200
                "
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <Button type="submit" variant="action">
                {selectedPlayer ? "Save Changes" : "Add Player"}
              </Button>
              {selectedPlayer && (
                <Button type="button" variant="danger" onClick={handleDelete}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Player
                </Button>
              )}
            </div>
          </form>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default AdminManagePlayerPage;
